export interface NicheAgentMetadata {
  id: string;
  name: string;
  endpoint: string;
  description: string;
  icon?: string;
  color?: string;
  subAgents: SubAgent[];
}

export interface SubAgent {
  id: string;
  name: string;
  description: string;
  detailDomain: string;
}

export interface NicheAgentStartRequest {
  initialPrompt: string;
}

export interface NicheAgentSession {
  sessionId: string;
  currentPrompt: string;
  iterations: NicheAgentIteration[];
  metadata?: Record<string, any>;
}

export interface NicheAgentIteration {
  iterationNumber: number;
  agentType: string;
  detailDomain?: string;
  previousPrompt: string;
  refinedPrompt: string;
  improvedPrompt?: string;
  improvementExplanation: string;
  timestamp: string;
}

export interface NicheAgentIterateRequest {
  agentType: string;
  detailDomain?: string;
  maxResponseTokens?: number;
  temperature?: number;
}

export interface NicheAgentBulkIterateRequest {
  agentType: 'Bulk';
  maxResponseTokens?: number;
  temperature?: number;
}

export interface NicheAgentCompactRequest {
  compactionLevel: 'Minimal' | 'Moderate' | 'Aggressive';
  targetTokenCount?: number;
}

export interface NicheAgentIterateResponse {
  sessionId: string;
  iterationNumber: number;
  refinedPrompt?: string;
  improvedPrompt?: string;
  improvementExplanation?: string;
  previousPrompt?: string;
  agentType: string;
  detailDomain?: string;
  success?: boolean;
  errors?: string | null;
}

export interface NicheAgentBulkIterateResponse {
  sessionId: string;
  iterations: NicheAgentIteration[];
  finalPrompt: string;
  totalIterations: number;
}

export interface NicheAgentCompactResponse {
  sessionId: string;
  originalPrompt?: string;
  compactedPrompt?: string;
  improvedPrompt?: string;
  originalTokenCount?: number;
  compactedTokenCount?: number;
  compressionRatio?: number;
  iterationNumber?: number;
  agentType?: string;
  detailDomain?: string;
  success?: boolean;
  errors?: string | null;
}

export const NICHE_AGENTS: NicheAgentMetadata[] = [
  {
    id: 'architecture',
    name: 'Architecture Visualization',
    endpoint: '/api/architecture',
    description: 'Architectural design and building visualization',
    color: '#1976d2',
    subAgents: [
      { id: 'structural', name: 'Structural Engineer', description: 'Load paths, materials, structural systems', detailDomain: 'structural' },
      { id: 'material', name: 'Material Specialist', description: 'Material properties and specifications', detailDomain: 'material' },
      { id: 'lighting', name: 'Lighting Designer', description: 'Natural and artificial lighting design', detailDomain: 'lighting' },
      { id: 'interior', name: 'Interior Architect', description: 'Space planning and interior design', detailDomain: 'interior' },
      { id: 'landscape', name: 'Landscape Integrator', description: 'Site integration and landscaping', detailDomain: 'landscape' },
      { id: 'facade', name: 'Facade Engineer', description: 'Building envelope design', detailDomain: 'facade' },
      { id: 'sustainability', name: 'Sustainability Analyst', description: 'Green building and energy efficiency', detailDomain: 'sustainability' },
      { id: 'acoustic', name: 'Acoustic Designer', description: 'Acoustic treatments and sound control', detailDomain: 'acoustic' },
      { id: 'hvac', name: 'HVAC Specialist', description: 'Climate control systems', detailDomain: 'hvac' },
      { id: 'cultural', name: 'Cultural Contextualizer', description: 'Cultural and historical context', detailDomain: 'cultural' }
    ]
  },
  {
    id: 'culinary',
    name: 'Culinary Experience',
    endpoint: '/api/culinary',
    description: 'Food visualization and culinary design',
    color: '#f57c00',
    subAgents: [
      { id: 'flavor', name: 'Flavor Chemist', description: 'Flavor profiles and chemical interactions', detailDomain: 'flavor' },
      { id: 'texture', name: 'Texture Specialist', description: 'Mouthfeel and texture combinations', detailDomain: 'texture' },
      { id: 'plating', name: 'Plating Artist', description: 'Visual presentation and plating design', detailDomain: 'plating' },
      { id: 'aroma', name: 'Aroma Composer', description: 'Scent profiles and aromatic elements', detailDomain: 'aroma' },
      { id: 'cultural', name: 'Cultural Authenticator', description: 'Traditional techniques and authenticity', detailDomain: 'cultural' },
      { id: 'molecular', name: 'Molecular Gastronomist', description: 'Modernist cooking techniques', detailDomain: 'molecular' },
      { id: 'temperature', name: 'Temperature Physicist', description: 'Heat transfer and cooking physics', detailDomain: 'temperature' },
      { id: 'nutrition', name: 'Nutrition Balancer', description: 'Nutritional optimization', detailDomain: 'nutrition' },
      { id: 'pairing', name: 'Pairing Specialist', description: 'Food and beverage pairings', detailDomain: 'pairing' },
      { id: 'technique', name: 'Technique Instructor', description: 'Cooking methods and procedures', detailDomain: 'technique' }
    ]
  },
  {
    id: 'environment',
    name: 'Environmental Simulation',
    endpoint: '/api/environment',
    description: 'Natural environment and ecosystem visualization',
    color: '#388e3c',
    subAgents: [
      { id: 'weather', name: 'Weather Dynamicist', description: 'Atmospheric conditions and weather', detailDomain: 'weather' },
      { id: 'terrain', name: 'Terrain Sculptor', description: 'Landforms and geological features', detailDomain: 'terrain' },
      { id: 'flora', name: 'Flora Specialist', description: 'Plant life and vegetation', detailDomain: 'flora' },
      { id: 'atmospheric', name: 'Atmospheric Renderer', description: 'Sky, clouds, and atmospheric effects', detailDomain: 'atmospheric' },
      { id: 'ecosystem', name: 'Ecosystem Balancer', description: 'Ecological relationships', detailDomain: 'ecosystem' },
      { id: 'light', name: 'Light Propagator', description: 'Natural lighting and shadows', detailDomain: 'light' },
      { id: 'fluid', name: 'Fluid Dynamicist', description: 'Water and fluid behavior', detailDomain: 'fluid' },
      { id: 'particle', name: 'Particle System Designer', description: 'Particles and environmental effects', detailDomain: 'particle' },
      { id: 'seasonal', name: 'Seasonal Transitioner', description: 'Seasonal changes and cycles', detailDomain: 'seasonal' },
      { id: 'geological', name: 'Geological Former', description: 'Rock formations and geology', detailDomain: 'geological' }
    ]
  },
  {
    id: 'mechanical',
    name: 'Mechanical Design',
    endpoint: '/api/mechanical',
    description: 'Mechanical systems and engineering visualization',
    color: '#616161',
    subAgents: [
      { id: 'kinematic', name: 'Kinematic Analyst', description: 'Motion analysis and mechanisms', detailDomain: 'kinematic' },
      { id: 'material', name: 'Material Engineer', description: 'Material selection and properties', detailDomain: 'material' },
      { id: 'power', name: 'Power System Designer', description: 'Power transmission systems', detailDomain: 'power' },
      { id: 'control', name: 'Control System Architect', description: 'Control and automation', detailDomain: 'control' },
      { id: 'aerodynamic', name: 'Aerodynamic Optimizer', description: 'Fluid dynamics and aerodynamics', detailDomain: 'aerodynamic' },
      { id: 'assembly', name: 'Assembly Specialist', description: 'Assembly and manufacturing', detailDomain: 'assembly' },
      { id: 'manufacturing', name: 'Manufacturing Analyst', description: 'Production processes', detailDomain: 'manufacturing' },
      { id: 'thermal', name: 'Thermal Engineer', description: 'Heat transfer and thermal management', detailDomain: 'thermal' },
      { id: 'tribology', name: 'Tribology Expert', description: 'Friction, wear, and lubrication', detailDomain: 'tribology' },
      { id: 'structural', name: 'Structural Analyst', description: 'Stress analysis and FEA', detailDomain: 'structural' }
    ]
  },
  {
    id: 'soundscape',
    name: 'Soundscape Composition',
    endpoint: '/api/soundscape',
    description: 'Audio environment and sound design',
    color: '#7b1fa2',
    subAgents: [
      { id: 'acoustic', name: 'Acoustic Modeler', description: 'Physical acoustics and room modeling', detailDomain: 'acoustic' },
      { id: 'harmonic', name: 'Harmonic Structurist', description: 'Musical harmony and theory', detailDomain: 'harmonic' },
      { id: 'rhythm', name: 'Rhythm Architect', description: 'Rhythmic patterns and timing', detailDomain: 'rhythm' },
      { id: 'spatial', name: 'Spatial Audio Designer', description: '3D audio and spatialization', detailDomain: 'spatial' },
      { id: 'emotional', name: 'Emotional Resonance Mapper', description: 'Emotional impact of sound', detailDomain: 'emotional' },
      { id: 'frequency', name: 'Frequency Analyst', description: 'Spectral analysis and EQ', detailDomain: 'frequency' },
      { id: 'reverb', name: 'Reverb Specialist', description: 'Reverberation and space', detailDomain: 'reverb' },
      { id: 'dynamics', name: 'Dynamics Processor', description: 'Dynamic range and compression', detailDomain: 'dynamics' },
      { id: 'timbre', name: 'Timbre Designer', description: 'Sound synthesis and timbre', detailDomain: 'timbre' },
      { id: 'psychoacoustic', name: 'Psychoacoustic Engineer', description: 'Human auditory perception', detailDomain: 'psychoacoustic' }
    ]
  },
  {
    id: 'textile',
    name: 'Textile Physics',
    endpoint: '/api/textile',
    description: 'Fabric simulation and textile visualization',
    color: '#e91e63',
    subAgents: [
      { id: 'fiber', name: 'Fiber Physicist', description: 'Fiber properties and mechanics', detailDomain: 'fiber' },
      { id: 'weave', name: 'Weave Pattern Master', description: 'Fabric construction patterns', detailDomain: 'weave' },
      { id: 'drape', name: 'Drape Dynamicist', description: 'Cloth simulation and draping', detailDomain: 'drape' },
      { id: 'historical', name: 'Historical Textile Expert', description: 'Traditional techniques', detailDomain: 'historical' },
      { id: 'surface', name: 'Surface Interaction Modeler', description: 'Friction and surface properties', detailDomain: 'surface' },
      { id: 'tensile', name: 'Tensile Analyst', description: 'Strength and mechanical testing', detailDomain: 'tensile' },
      { id: 'color', name: 'Color Fastness Expert', description: 'Dyeing and coloration', detailDomain: 'color' },
      { id: 'texture', name: 'Texture Simulator', description: 'Surface texture and feel', detailDomain: 'texture' },
      { id: 'environmental', name: 'Environmental Response Modeler', description: 'Moisture and thermal behavior', detailDomain: 'environmental' },
      { id: 'manufacturing', name: 'Manufacturing Process Specialist', description: 'Production methods', detailDomain: 'manufacturing' }
    ]
  },
  {
    id: 'botanical',
    name: 'Botanical Illustration',
    endpoint: '/api/botanical',
    description: 'Scientific plant visualization and botanical art',
    color: '#4caf50',
    subAgents: [
      { id: 'morphology', name: 'Morphology Expert', description: 'Plant structure and form', detailDomain: 'morphology' },
      { id: 'growth', name: 'Growth Pattern Analyst', description: 'Development and phenology', detailDomain: 'growth' },
      { id: 'color', name: 'Color Pigment Specialist', description: 'Plant pigments and coloration', detailDomain: 'color' },
      { id: 'symbiosis', name: 'Symbiosis Renderer', description: 'Symbiotic relationships', detailDomain: 'symbiosis' },
      { id: 'seasonal', name: 'Seasonal Transitioner', description: 'Seasonal changes', detailDomain: 'seasonal' },
      { id: 'taxonomy', name: 'Taxonomy Specialist', description: 'Classification and identification', detailDomain: 'taxonomy' },
      { id: 'cellular', name: 'Cellular Structure Expert', description: 'Microscopic details', detailDomain: 'cellular' },
      { id: 'pollination', name: 'Pollination Biologist', description: 'Reproductive biology', detailDomain: 'pollination' },
      { id: 'ecological', name: 'Ecological Contextualizer', description: 'Habitat and ecology', detailDomain: 'ecological' },
      { id: 'phytochemistry', name: 'Phytochemistry Analyst', description: 'Plant chemistry', detailDomain: 'phytochemistry' }
    ]
  },
  {
    id: 'marine',
    name: 'Marine Biology',
    endpoint: '/api/marine',
    description: 'Marine life and underwater ecosystem visualization',
    color: '#0288d1',
    subAgents: [
      { id: 'organism', name: 'Marine Organism Expert', description: 'Marine species and biological structures', detailDomain: 'organism' },
      { id: 'oceanography', name: 'Oceanography Specialist', description: 'Ocean currents and water properties', detailDomain: 'oceanography' },
      { id: 'bioluminescence', name: 'Bioluminescence Renderer', description: 'Light-producing marine organisms', detailDomain: 'bioluminescence' },
      { id: 'coral', name: 'Coral Reef Architect', description: 'Coral structures and reef ecosystems', detailDomain: 'coral' },
      { id: 'deepsea', name: 'Deep Sea Adaptation Expert', description: 'Deep ocean environments and adaptations', detailDomain: 'deepsea' },
      { id: 'behavior', name: 'Marine Behavior Analyst', description: 'Animal behavior and interactions', detailDomain: 'behavior' },
      { id: 'conservation', name: 'Conservation Visualizer', description: 'Conservation issues and threats', detailDomain: 'conservation' },
      { id: 'pelagic', name: 'Pelagic Zone Specialist', description: 'Open ocean environments', detailDomain: 'pelagic' },
      { id: 'symbiosis', name: 'Marine Symbiosis Illustrator', description: 'Symbiotic relationships', detailDomain: 'symbiosis' },
      { id: 'photography', name: 'Underwater Photography Expert', description: 'Underwater imaging techniques', detailDomain: 'photography' }
    ]
  },
  {
    id: 'astronomical',
    name: 'Astronomical Visualization',
    endpoint: '/api/astronomical',
    description: 'Space, celestial bodies, and cosmic phenomena visualization',
    color: '#311b92',
    subAgents: [
      { id: 'stellar', name: 'Stellar Astronomer', description: 'Star properties and stellar evolution', detailDomain: 'stellar' },
      { id: 'galactic', name: 'Galactic Structure Expert', description: 'Galaxy morphology and structure', detailDomain: 'galactic' },
      { id: 'cosmological', name: 'Cosmological Visualizer', description: 'Large-scale universe structure', detailDomain: 'cosmological' },
      { id: 'planetary', name: 'Planetary Scientist', description: 'Planets and solar system objects', detailDomain: 'planetary' },
      { id: 'deepspace', name: 'Deep Space Phenomena Renderer', description: 'Black holes, quasars, and exotic objects', detailDomain: 'deepspace' },
      { id: 'spectral', name: 'Spectral Analyst', description: 'Electromagnetic radiation across wavelengths', detailDomain: 'spectral' },
      { id: 'relativistic', name: 'Relativistic Effects Modeler', description: 'Gravitational and relativistic phenomena', detailDomain: 'relativistic' },
      { id: 'observational', name: 'Observational Astronomer', description: 'Telescope capabilities and techniques', detailDomain: 'observational' },
      { id: 'cosmic', name: 'Cosmic Scale Navigator', description: 'Distance and scale relationships', detailDomain: 'cosmic' },
      { id: 'exploration', name: 'Space Exploration Visualizer', description: 'Spacecraft and exploration technology', detailDomain: 'exploration' }
    ]
  },
  {
    id: 'medical',
    name: 'Medical Illustration',
    endpoint: '/api/medical',
    description: 'Anatomical and medical visualization with scientific accuracy',
    color: '#d32f2f',
    subAgents: [
      { id: 'anatomical', name: 'Anatomical Accuracy Expert', description: 'Precise anatomical structures', detailDomain: 'anatomical' },
      { id: 'surgical', name: 'Surgical Visualization Specialist', description: 'Surgical procedures and techniques', detailDomain: 'surgical' },
      { id: 'pathological', name: 'Pathological Illustrator', description: 'Disease processes and abnormalities', detailDomain: 'pathological' },
      { id: 'molecular', name: 'Molecular Visualization Expert', description: 'Cellular and molecular structures', detailDomain: 'molecular' },
      { id: 'patient', name: 'Patient Education Designer', description: 'Accessible medical education materials', detailDomain: 'patient' },
      { id: 'animation', name: 'Medical Animation Creator', description: 'Dynamic medical visualizations', detailDomain: 'animation' },
      { id: 'clinical', name: 'Clinical Documentation Specialist', description: 'Medical record illustrations', detailDomain: 'clinical' },
      { id: 'interactive', name: 'Interactive Media Developer', description: 'VR/AR medical applications', detailDomain: 'interactive' },
      { id: 'pharmaceutical', name: 'Pharmaceutical Visualization Expert', description: 'Drug mechanisms and interactions', detailDomain: 'pharmaceutical' },
      { id: 'quality', name: 'Quality Assurance Validator', description: 'Medical accuracy and compliance', detailDomain: 'quality' }
    ]
  },
  {
    id: 'quantumphysics',
    name: 'Quantum Physics Visualization',
    endpoint: '/api/quantumphysics',
    description: 'Quantum mechanical phenomena and particle physics visualization',
    color: '#00bcd4',
    subAgents: [
      { id: 'wavefunction', name: 'Wave Function Visualizer', description: 'Wave functions and probability distributions', detailDomain: 'wavefunction' },
      { id: 'particle', name: 'Particle Interaction Modeler', description: 'Feynman diagrams and particle interactions', detailDomain: 'particle' },
      { id: 'quantum', name: 'Quantum State Engineer', description: 'Superposition and entanglement visualization', detailDomain: 'quantum' },
      { id: 'atomic', name: 'Atomic Orbital Designer', description: 'Electron orbitals and atomic structures', detailDomain: 'atomic' },
      { id: 'circuit', name: 'Quantum Circuit Architect', description: 'Quantum computing circuits and gates', detailDomain: 'circuit' },
      { id: 'entanglement', name: 'Entanglement Illustrator', description: 'Quantum correlations and Bell states', detailDomain: 'entanglement' },
      { id: 'field', name: 'Field Theory Renderer', description: 'Quantum field theory visualizations', detailDomain: 'field' },
      { id: 'measurement', name: 'Measurement Process Modeler', description: 'Measurement and decoherence', detailDomain: 'measurement' },
      { id: 'condensed', name: 'Condensed Matter Visualizer', description: 'Many-body quantum phenomena', detailDomain: 'condensed' },
      { id: 'relativistic', name: 'Relativistic Quantum Expert', description: 'Relativistic quantum mechanics', detailDomain: 'relativistic' }
    ]
  },
  {
    id: 'urban',
    name: 'Urban Planning',
    endpoint: '/api/urban',
    description: 'Urban design, city planning, and infrastructure visualization',
    color: '#795548',
    subAgents: [
      { id: 'zoning', name: 'Zoning Strategist', description: 'Land use regulations and development standards', detailDomain: 'zoning' },
      { id: 'transportation', name: 'Transportation Planner', description: 'Multi-modal transportation networks', detailDomain: 'transportation' },
      { id: 'infrastructure', name: 'Infrastructure Engineer', description: 'Urban utilities and civil engineering systems', detailDomain: 'infrastructure' },
      { id: 'greenspace', name: 'Green Space Designer', description: 'Parks, urban forests, and green infrastructure', detailDomain: 'greenspace' },
      { id: 'housing', name: 'Housing Developer', description: 'Residential design and affordability strategies', detailDomain: 'housing' },
      { id: 'economic', name: 'Economic Development Analyst', description: 'Commercial development and job creation', detailDomain: 'economic' },
      { id: 'publicspace', name: 'Public Space Architect', description: 'Plazas, streets, and social infrastructure', detailDomain: 'publicspace' },
      { id: 'smartcity', name: 'Smart City Technologist', description: 'Digital infrastructure and IoT integration', detailDomain: 'smartcity' },
      { id: 'sustainability', name: 'Sustainability Coordinator', description: 'Environmental planning and climate adaptation', detailDomain: 'sustainability' },
      { id: 'community', name: 'Community Engagement Facilitator', description: 'Participatory planning and stakeholder coordination', detailDomain: 'community' }
    ]
  },
  {
    id: 'forensic',
    name: 'Forensic Science',
    endpoint: '/api/forensic',
    description: 'Crime scene analysis, forensic techniques, and investigative procedures',
    color: '#37474f',
    subAgents: [
      { id: 'crimescene', name: 'Crime Scene Analyst', description: 'Scene processing and evidence collection protocols', detailDomain: 'crimescene' },
      { id: 'dna', name: 'DNA Forensicist', description: 'Genetic analysis and biological evidence interpretation', detailDomain: 'dna' },
      { id: 'toxicology', name: 'Toxicology Expert', description: 'Drug analysis and poison detection', detailDomain: 'toxicology' },
      { id: 'ballistics', name: 'Ballistics Specialist', description: 'Firearms examination and trajectory analysis', detailDomain: 'ballistics' },
      { id: 'digital', name: 'Digital Forensics Expert', description: 'Computer evidence and cybercrime investigation', detailDomain: 'digital' },
      { id: 'document', name: 'Document Examiner', description: 'Handwriting analysis and document authentication', detailDomain: 'document' },
      { id: 'bloodpattern', name: 'Blood Pattern Analyst', description: 'Bloodstain interpretation and impact analysis', detailDomain: 'bloodpattern' },
      { id: 'fingerprint', name: 'Fingerprint Examiner', description: 'Latent print development and pattern recognition', detailDomain: 'fingerprint' },
      { id: 'pathology', name: 'Forensic Pathologist', description: 'Autopsy procedures and cause of death determination', detailDomain: 'pathology' },
      { id: 'legal', name: 'Legal Forensics Coordinator', description: 'Chain of custody and court testimony standards', detailDomain: 'legal' }
    ]
  },
  {
    id: 'geological',
    name: 'Geological Survey',
    endpoint: '/api/geological',
    description: 'Earth sciences, geological features, and resource exploration visualization',
    color: '#8d6e63',
    subAgents: [
      { id: 'structural', name: 'Structural Geologist', description: 'Fault systems, fold structures, and tectonic analysis', detailDomain: 'structural' },
      { id: 'mineralogist', name: 'Mineralogist', description: 'Mineral identification, crystal structures, and mineral chemistry', detailDomain: 'mineralogist' },
      { id: 'sedimentologist', name: 'Sedimentologist', description: 'Sedimentary processes, depositional environments, and basin evolution', detailDomain: 'sedimentologist' },
      { id: 'petrologist', name: 'Petrologist', description: 'Igneous and metamorphic rocks, magma evolution, and P-T conditions', detailDomain: 'petrologist' },
      { id: 'geophysicist', name: 'Geophysicist', description: 'Seismic, gravity, magnetic methods for subsurface investigation', detailDomain: 'geophysicist' },
      { id: 'stratigrapher', name: 'Stratigrapher', description: 'Rock layer relationships, geological time, and correlation techniques', detailDomain: 'stratigrapher' },
      { id: 'geomorphologist', name: 'Geomorphologist', description: 'Landforms, surface processes, and landscape evolution', detailDomain: 'geomorphologist' },
      { id: 'economic', name: 'Economic Geologist', description: 'Ore deposits, petroleum systems, and mineral resources', detailDomain: 'economic' },
      { id: 'hydrogeologist', name: 'Hydrogeologist', description: 'Groundwater systems, aquifer properties, and water chemistry', detailDomain: 'hydrogeologist' },
      { id: 'environmental', name: 'Environmental Geologist', description: 'Geohazards, climate records, and geological risk assessment', detailDomain: 'environmental' }
    ]
  },
  {
    id: 'aerospace',
    name: 'Aerospace Engineering',
    endpoint: '/api/aerospace',
    description: 'Aircraft, spacecraft, and propulsion system design and visualization',
    color: '#1565c0',
    subAgents: [
      { id: 'aerodynamics', name: 'Aerodynamics Expert', description: 'Airflow analysis, CFD modeling, and aerodynamic optimization', detailDomain: 'aerodynamics' },
      { id: 'propulsion', name: 'Propulsion Engineer', description: 'Jet engines, rocket motors, and propulsion integration', detailDomain: 'propulsion' },
      { id: 'structural', name: 'Structural Analyst', description: 'Aerospace structures, stress analysis, and weight optimization', detailDomain: 'structural' },
      { id: 'flight', name: 'Flight Dynamicist', description: 'Aircraft stability, control laws, and handling qualities', detailDomain: 'flight' },
      { id: 'spacecraft', name: 'Spacecraft Designer', description: 'Orbital vehicles, satellite systems, and mission design', detailDomain: 'spacecraft' },
      { id: 'avionics', name: 'Avionics Specialist', description: 'Flight control systems, navigation, and cockpit design', detailDomain: 'avionics' },
      { id: 'systems', name: 'Systems Integrator', description: 'Subsystem interaction, requirements, and certification', detailDomain: 'systems' },
      { id: 'aerothermo', name: 'Aerothermodynamicist', description: 'High-speed flight, heat transfer, and thermal protection', detailDomain: 'aerothermo' },
      { id: 'test', name: 'Test Engineer', description: 'Flight testing, ground testing, and validation procedures', detailDomain: 'test' },
      { id: 'mission', name: 'Mission Planner', description: 'Trajectory optimization, payload integration, and operations', detailDomain: 'mission' }
    ]
  },
  {
    id: 'neurological',
    name: 'Neurological Mapping',
    endpoint: '/api/neurological',
    description: 'Brain anatomy, neural pathways, and neurological visualization',
    color: '#ad1457',
    subAgents: [
      { id: 'neuroanatomist', name: 'Neuroanatomist Expert', description: 'Brain structures, anatomical landmarks, and 3D relationships', detailDomain: 'neuroanatomist' },
      { id: 'pathway', name: 'Neural Pathway Tracer', description: 'White matter tracts, neural circuits, and connectome mapping', detailDomain: 'pathway' },
      { id: 'cellular', name: 'Cellular Neurobiologist', description: 'Neuronal morphology, synaptic structures, and glial cells', detailDomain: 'cellular' },
      { id: 'neurophysiology', name: 'Neurophysiology Modeler', description: 'Neural signaling, action potentials, and electrophysiology', detailDomain: 'neurophysiology' },
      { id: 'clinical', name: 'Clinical Neurologist', description: 'Neurological disorders, imaging interpretation, and diagnosis', detailDomain: 'clinical' },
      { id: 'development', name: 'Neurodevelopment Specialist', description: 'Brain development, synaptogenesis, and critical periods', detailDomain: 'development' },
      { id: 'cognitive', name: 'Cognitive Neuroscientist', description: 'Functional networks, cognitive domains, and brain-behavior', detailDomain: 'cognitive' },
      { id: 'neuroimaging', name: 'Neuroimaging Analyst', description: 'MRI sequences, functional imaging, and image processing', detailDomain: 'neuroimaging' },
      { id: 'neuropharmacology', name: 'Neuropharmacology Expert', description: 'Neurotransmitter systems, drug mechanisms, and therapies', detailDomain: 'neuropharmacology' },
      { id: 'neurorehab', name: 'Neurorehabilition Designer', description: 'Plasticity mechanisms, recovery strategies, and technologies', detailDomain: 'neurorehab' }
    ]
  },
  {
    id: 'archaeological',
    name: 'Archaeological Reconstruction',
    endpoint: '/api/archaeological',
    description: 'Ancient sites, artifacts, cultures, and historical environment reconstruction',
    color: '#6d4c41',
    subAgents: [
      { id: 'field', name: 'Field Archaeologist', description: 'Excavation methods, stratigraphy, context recording, field documentation', detailDomain: 'field' },
      { id: 'material', name: 'Material Culture Expert', description: 'Artifact analysis, typology, manufacturing techniques, cultural attribution', detailDomain: 'material' },
      { id: 'architectural', name: 'Architectural Historian', description: 'Ancient building techniques, structural analysis, spatial organization', detailDomain: 'architectural' },
      { id: 'bioarchaeology', name: 'Bioarchaeologist Specialist', description: 'Human remains analysis, paleopathology, mortuary practices', detailDomain: 'bioarchaeology' },
      { id: 'environmental', name: 'Environmental Archaeologist', description: 'Paleoenvironmental reconstruction, subsistence strategies, human-environment interactions', detailDomain: 'environmental' },
      { id: 'chronology', name: 'Chronology Specialist', description: 'Dating methods, temporal frameworks, chronological sequence building', detailDomain: 'chronology' },
      { id: 'conservation', name: 'Conservation Scientist', description: 'Artifact preservation, material analysis, preventive conservation', detailDomain: 'conservation' },
      { id: 'digital', name: 'Digital Archaeologist', description: '3D documentation, geophysical survey, GIS analysis, virtual reconstruction', detailDomain: 'digital' },
      { id: 'heritage', name: 'Cultural Heritage Manager', description: 'Site preservation, stakeholder engagement, sustainable heritage management', detailDomain: 'heritage' },
      { id: 'public', name: 'Public Archaeology Coordinator', description: 'Community engagement, interpretation, education, ethical practice', detailDomain: 'public' }
    ]
  },
  {
    id: 'climate',
    name: 'Climate Modeling',
    endpoint: '/api/climate',
    description: 'Climate systems, atmospheric processes, weather patterns, and environmental change visualization',
    color: '#00acc1',
    subAgents: [
      { id: 'atmospheric', name: 'Atmospheric Physicist', description: 'Atmospheric dynamics, thermodynamics, radiation physics, fundamental processes', detailDomain: 'atmospheric' },
      { id: 'oceanographer', name: 'Oceanographer Modeler', description: 'Ocean circulation, marine dynamics, air-sea interactions, coupled systems', detailDomain: 'oceanographer' },
      { id: 'cryosphere', name: 'Cryosphere Specialist', description: 'Ice sheet dynamics, sea ice processes, glaciers, permafrost, frozen components', detailDomain: 'cryosphere' },
      { id: 'weather', name: 'Weather Systems Analyst', description: 'Synoptic meteorology, severe weather, tropical cyclones, mesoscale phenomena', detailDomain: 'weather' },
      { id: 'data', name: 'Climate Data Scientist', description: 'Climate data analysis, statistical methods, machine learning, visualization', detailDomain: 'data' },
      { id: 'earthsystem', name: 'Earth System Modeler', description: 'Coupled Earth system models, component interactions, feedbacks, integrated assessments', detailDomain: 'earthsystem' },
      { id: 'extreme', name: 'Extreme Events Expert', description: 'Extreme weather and climate events, risk assessment, attribution, impact analysis', detailDomain: 'extreme' },
      { id: 'paleoclimate', name: 'Paleoclimate Researcher', description: 'Past climate reconstructions, proxy interpretation, climate sensitivity, analog periods', detailDomain: 'paleoclimate' },
      { id: 'regional', name: 'Regional Climate Analyst', description: 'Regional climate processes, downscaling methods, local impacts, place-based assessments', detailDomain: 'regional' },
      { id: 'communication', name: 'Climate Comms Specialist', description: 'Climate communication, visualization best practices, stakeholder engagement, education', detailDomain: 'communication' }
    ]
  }
];